"use client";

import Logger from "@/lib/utils/logger";

// This module ensures that there is only one instance of the HTMLAudioElement
// used for playing assistant audio throughout the application. This prevents multiple
// assistant voice clips from playing simultaneously and from interfering with the main music player.

let audioInstance: HTMLAudioElement | null = null;

const getAssistantAudioPlayer = (): HTMLAudioElement => {
  if (typeof window !== "undefined" && !audioInstance) {
    Logger.debug("🔊 Creating new assistant audio player instance.");
    audioInstance = new Audio();
    audioInstance.preload = "metadata";
  }

  if (!audioInstance) {
    throw new Error("Audio player not available in this environment");
  }

  return audioInstance;
};

// Export the function instead of calling it at module level to avoid SSR issues
export { getAssistantAudioPlayer };

/**
 * Normalizes volume from 0-100 range to 0-1 range with proper bounds checking
 * @param volume Volume in 0-100 range, or undefined for default
 * @returns Volume in 0-1 range (0.0 to 1.0)
 */
const normalizeVolume = (volume: number | undefined): number => {
  // Use default of 50 if volume is undefined
  const volumeValue = volume ?? 50;
  // Round to integer to ensure whole number percentage
  const rounded = Math.round(volumeValue);
  // Clamp to 0-100 range
  const clamped = Math.max(0, Math.min(100, rounded));
  // Convert to 0-1 range for HTMLAudioElement
  return clamped / 100;
};

// Provide a singleton accessor that's safe for SSR
export const assistantAudioPlayer = {
  getInstance: (): HTMLAudioElement => {
    return getAssistantAudioPlayer();
  },

  /**
   * Sets the volume for the assistant audio player
   * @param volume Volume in 0-100 range, or undefined for default (50)
   */
  setVolume: (volume: number | undefined): void => {
    const audio = getAssistantAudioPlayer();
    audio.volume = normalizeVolume(volume);
    Logger.debug(`[AssistantAudioPlayer] Volume set to: ${normalizeVolume(volume)} (from ${volume ?? 50}/100)`);
  },
};
